import { createContext, useContext, useEffect, useRef, useState } from "react";
import { MEAL_TYPE_MAP } from "../config/meal-config";
import { buildEditableMeal, normalizeEditableItem, normalizeMealItems } from "../helpers";
import { itemsStructurallyEqual } from "../helpers/domain";

// HELPERS

const resolveSelectedEntry = (entryType, meals, exerciseSession, mealId) => {
	if (entryType == null) return null;

	if (entryType === "exercise") {
		return {
			type: "exercise",
			id: exerciseSession?.id ?? null,
			items: exerciseSession?.exercises ?? [],
		};
	}

	const mealType = Number(entryType);
	const mealKey = MEAL_TYPE_MAP[mealType];

	if (!mealKey) {
		console.warn("Unknown mealType in resolveSelectedEntry:", entryType);
		return null;
	}

	const meal = meals?.[mealKey]?.find((m) => m.meal_id === mealId);

	if (!meal) return null;

	return {
		type: mealType,
		id: meal.meal_id,
		favorite_id: meal.favorite_id ?? null,
		recipe_id: meal.recipe_id ?? null,
		items: meal.items ?? [],
		name: meal.name ?? "",
		time: meal.time ?? "",
	};
};

// STATE

export const EMPTY_EDITABLE_MEAL = {
	id: null,
	items: [],
	name: "",
	time: "",
	isFavorite: false,
	isRecipe: false,
	servings: null,
};

const ModalEntryContext = createContext(null);

// PROVIDER

export const ModalEntryProvider = ({
	entryType,
	mealId,
	meals,
	exerciseSession,
	initialPayload,
	children,
}) => {
	const [selectedEntry, setSelectedEntry] = useState(null);
	const [editableMeal, setEditableMeal] = useState(EMPTY_EDITABLE_MEAL);

	const selectedEntryId = selectedEntry?.id ?? null;

	const hydratedEntryIdRef = useRef(null);
	const [templateSnapshot, setTemplateSnapshot] = useState(null);

	// --- RESOLVE SELECTED ENTRY (READ-ONLY, DB STATE) ---

	useEffect(() => {
		setSelectedEntry(resolveSelectedEntry(entryType, meals, exerciseSession, mealId));
	}, [entryType, meals, exerciseSession, mealId]);

	// --- EDITABLE MEAL (DRAFT STATE) ---

	// SETTERS

	const addEditableItems = (items) => {

		setEditableMeal((prev) => {
			const next = buildEditableMeal(prev, items);

			return {
				...next,
				name: next?.name ?? '',
				isFavorite: false,
				isRecipe: false,
				id: prev.id,
			};
		});
	};

	// TEMPLATE MEALS

	const createFromFavorite = (favorite) => {

		const templateItems = normalizeMealItems(favorite)
			.map((item) => ({
				...item,
				_favoriteOrigin: true,
			}))
			.map((item) => normalizeEditableItem(item, true));

		setTemplateSnapshot({
			type: "favorite",
			items: templateItems,
			name: favorite?.name ?? "",
		});

		setEditableMeal((prev) => {

            const next = buildEditableMeal(prev, templateItems)

            return {
                ...next,
                id: favorite?.id ?? prev.id,
                name: prev?.name ?? favorite?.name ?? "",
                isFavorite: true,
                isRecipe: false,
            }

        });
	};

	const createFromRecipe = (recipe) => {
		const templateItems = normalizeMealItems(recipe)
			.map((item) => ({
				...item,
				_recipeOrigin: true,
			}))
			.map((item) => normalizeEditableItem(item, true));

		setTemplateSnapshot({
			type: "recipe",
			items: templateItems,
			name: recipe?.name ?? "",
			image: recipe?.image ?? null,
		});

		setEditableMeal((prev) => {

            const next = buildEditableMeal(prev, templateItems)

            return {
                ...next,
                id: recipe?.id ?? prev.id,
                name: prev?.name ?? recipe?.name ?? "",
                isFavorite: false,
                isRecipe: true,
                servings: 1,
            }

        });
	};

	// DETECT TEMPLATE MEAL MISMATCH

	useEffect(() => {
		if (!templateSnapshot?.type) return;

		const itemsMatch = itemsStructurallyEqual(editableMeal.items, templateSnapshot.items);

		if (!itemsMatch && (editableMeal.isFavorite || editableMeal.isRecipe)) {

			setEditableMeal((prev) => {

                return {
                    ...prev,
                    name: prev?.name ?? "",
                    isFavorite: false,
                    isRecipe: false,
                }

            });

			return;
		}

		if (!itemsMatch) return;

		const shouldBeFavorite = templateSnapshot.type === "favorite";
		const shouldBeRecipe = templateSnapshot.type === "recipe";

		const isFavoriteChanged = editableMeal.isFavorite !== shouldBeFavorite;
		const isRecipeChanged = editableMeal.isRecipe !== shouldBeRecipe;
		const nameChanged = editableMeal.name !== templateSnapshot.name;

		if (isFavoriteChanged || isRecipeChanged || nameChanged) {
			setEditableMeal((prev) => ({
				...prev,
				name: templateSnapshot.name,
				isFavorite: shouldBeFavorite,
				isRecipe: shouldBeRecipe,
			}));
		}
	}, [editableMeal.items, templateSnapshot]);

	// INIT

	useEffect(() => {
		if (!selectedEntry) return;
		if (hydratedEntryIdRef.current === selectedEntryId) return;

		const entryForInit = initialPayload ? { ...selectedEntry, ...initialPayload } : selectedEntry;

		const hydratedItems = (entryForInit.items ?? []).map((item) => normalizeEditableItem(item, false));

		const isPersistedTemplate = selectedEntry.favorite_id || selectedEntry.recipe_id;

		if (isPersistedTemplate) {
			setTemplateSnapshot({
				type: selectedEntry.favorite_id ? "favorite" : "recipe",
				items: hydratedItems,
				name: selectedEntry.name ?? "",
				image: entryForInit.image ?? null,
			});
		} else setTemplateSnapshot(null);

		setEditableMeal({
			...EMPTY_EDITABLE_MEAL,
			...entryForInit,
			items: hydratedItems,
			isFavorite: Boolean(selectedEntry.favorite_id),
			isRecipe: Boolean(selectedEntry.recipe_id),
			id: selectedEntryId,
		});

		hydratedEntryIdRef.current = selectedEntryId;
	}, [selectedEntryId, initialPayload]);

	return (
		<ModalEntryContext.Provider
			value={{
				selectedEntry,
				selectedEntryId,
				editableMeal,
				templateSnapshot,
				setEditableMeal,
				addEditableItems,
				createFromFavorite,
				createFromRecipe,
				setTemplateSnapshot,
			}}
		>
			{children}
		</ModalEntryContext.Provider>
	);
};

// HOOKS

export const useModalEntry = () => {
	const ctx = useContext(ModalEntryContext);

	if (!ctx) throw new Error("useModalEntry must be used inside ModalEntryProvider");

	return {
		selectedEntry: ctx.selectedEntry,
		selectedEntryId: ctx.selectedEntryId,
	};
};

export const useEditableMeal = () => {
	const ctx = useContext(ModalEntryContext);

	if (!ctx) throw new Error("useEditableMeal must be used inside ModalEntryProvider");

	return {
		editableMeal: ctx.editableMeal,
		setEditableMeal: ctx.setEditableMeal,
		addEditableItems: ctx.addEditableItems,
		setTemplateSnapshot: ctx.setTemplateSnapshot,
		templateSnapshot: ctx.templateSnapshot,
		createFromFavorite: ctx.createFromFavorite,
		createFromRecipe: ctx.createFromRecipe,
	};
};

export default ModalEntryContext;
